import React, { useState, useRef, useEffect } from "react";
import { Observer } from "mobx-react";
import { MdOutlineCameraAlt } from "react-icons/md";
import { useStores } from "../../store/index";
import { uploadFileToAzure } from "../../services/azure";

export default function ImageCapture({
  popUpInitialValue,
  handlePopup,
  onImageCaptured,
}) {
  const { PopupStore } = useStores();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [imageCaptured, setImageCaptured] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraPermission, setCameraPermission] = useState(null);
  const [imageBlob, setImageBlob] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    if (popUpInitialValue) {
      setIsLoading(true);
      handleStartVideo();
    }
    return () => {
      stopCamera();
    };
  }, [popUpInitialValue]);

  const handleStartVideo = async () => {
    try {
      // First check if we have permission
      const permissionStatus = await navigator.permissions.query({
        name: "camera",
      });
      setCameraPermission(permissionStatus.state);

      if (permissionStatus.state === "denied") {
        // If permission was previously denied, show a message
        alert(
          "Camera access is required to capture your image. Please enable camera access in your browser settings."
        );
        return false;
      }

      // Try to get camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user"
        },
        audio: false,
      });

      setStream(stream);
      videoRef.current.srcObject = stream;
      videoRef.current.style.transform = "scaleX(-1)"; // Mirror the video horizontally
      
      // Wait for video to be ready
      videoRef.current.onloadedmetadata = () => {
        setIsVideoReady(true);
        setIsLoading(false);
      };
      
      return true;
    } catch (error) {
      console.error("Error accessing camera:", error);
      if (error.name === "NotAllowedError") {
        alert("Please allow camera access to capture your image.");
      } else {
        alert(
          "Error accessing your camera. Please check your camera settings."
        );
      }
      setIsLoading(false);
      return false;
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setIsVideoReady(false);
  };

  const captureImage = async () => {
    console.log("Capture image called");
    console.log("Video ready:", isVideoReady);
    console.log("Video ref:", videoRef.current);
    console.log("Canvas ref:", canvasRef.current);
    
    if (!isVideoReady || !videoRef.current || !canvasRef.current) {
      console.log("Video not ready or refs not available");
      alert("Please wait for the camera to be ready before capturing.");
      return;
    }

    const permissionStatus = await navigator.permissions.query({
      name: "camera",
    });
    if (permissionStatus.state === "denied") {
      alert(
        "Camera access is required to capture your image. Please enable camera access in your browser settings."
      );
      return;
    }

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      // Get the container dimensions
      const containerRect = containerRef.current.getBoundingClientRect();
      const displayWidth = containerRect.width;
      const displayHeight = containerRect.height;

      console.log("Container dimensions:", { displayWidth, displayHeight });
      console.log("Video dimensions:", { width: video.videoWidth, height: video.videoHeight });

      // Set canvas dimensions to match the displayed video size
      canvas.width = displayWidth;
      canvas.height = displayHeight;

      // Clear the canvas
      context.clearRect(0, 0, canvas.width, canvas.height);

      // Save the current context state
      context.save();

      // Flip the context horizontally to match the mirrored video
      context.scale(-1, 1);
      context.translate(-canvas.width, 0);

      // Draw the video frame to canvas
      // Use the full canvas dimensions since we're already handling the aspect ratio with CSS object-contain
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Restore the context state
      context.restore();

      console.log("Canvas drawn successfully");

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          console.log("Blob created:", blob.size, "bytes");
          const imageUrl = URL.createObjectURL(blob);
          setCapturedImage(imageUrl);
          setImageCaptured(true);
          setImageBlob(blob);
        } else {
          console.error("Failed to create blob from canvas");
          alert("Failed to capture image. Please try again.");
        }
      }, "image/jpeg", 0.9);
    } catch (error) {
      console.error("Error capturing image:", error);
      alert("Error capturing image. Please try again.");
    }
  };

  const retakeImage = () => {
    setImageCaptured(false);
    setCapturedImage(null);
    setImageBlob(null);
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
    }
    handleStartVideo();
  };

  return (
    <Observer>
      {() => (
        <>
          {popUpInitialValue && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-[90%] md:w-[500px]">
                <h2 className="text-2xl font-bold mb-4 text-center">
                  Capture Your Image
                </h2>
                <p className="text-gray-600 mb-4 text-center">
                  Please take a clear photo of yourself before starting the
                  interview
                </p>

                <div 
                  ref={containerRef}
                  className="relative w-full aspect-video bg-gray-200 rounded-lg overflow-hidden mb-4"
                >
                  {!imageCaptured ? (
                    <div className="w-full h-full">
                      <video
                        ref={videoRef}
                        className="w-full h-full object-contain rounded-lg"
                        autoPlay
                        playsInline
                        muted
                        style={{ transform: "scaleX(-1)" }}
                      />
                      {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <img
                      src={capturedImage}
                      alt="Captured"
                      className="w-full h-full object-contain rounded-lg"
                    />
                  )}
                  <canvas ref={canvasRef} className="hidden" />
                </div>

                <div className="flex justify-center gap-4">
                  {!imageCaptured ? (
                    <button
                      onClick={captureImage}
                      className="flex items-center gap-2 bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isLoading || !isVideoReady}
                    >
                      {isLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <MdOutlineCameraAlt className="text-xl" />
                      )}
                      {isLoading ? "Loading..." : isVideoReady ? "Capture" : "Preparing..."}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={retakeImage}
                        className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        Retake
                      </button>
                      <button
                        onClick={async () => {
                          setIsUploading(true);
                          setUploadProgress(0);
                          try {
                            const response = await uploadFileToAzure(
                              `image-${Date.now()}.png`,
                              imageBlob,
                              { action: "recording" },
                              (percentCompleted) => {
                                setUploadProgress(percentCompleted);
                              }
                            );
                            console.log("imageUrl", response);
                            onImageCaptured(response?.url);
                            stopCamera();
                          } catch (error) {
                            console.error("Upload error:", error);
                            alert("Failed to upload image. Please try again.");
                          } finally {
                            setIsUploading(false);
                          }
                        }}
                        className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition-colors"
                        disabled={isUploading}
                      >
                        {isUploading
                          ? `Uploading... ${uploadProgress}%`
                          : "Continue"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </Observer>
  );
}
